/**
 * CountrySelector Web Component
 *
 * 功能说明：
 * 用于在页面中渲染并控制国家 / 语言 / 货币选择弹窗。
 *
 * 工作流程：
 * 1. Web Component 挂载后执行 init。
 * 2. 从 <country-selector> 组件内部读取 Liquid 渲染内容。
 * 3. 将组件内部 HTML 复制到页面目标容器 `.country-selecor` 中。
 * 4. 根据当前 URL path 匹配 `.country-selector-market` 的 data-parameter，
 *    动态更新国家图片、语言和货币信息。
 * 5. 使用 CSS class `is-open` 控制弹窗显隐（opacity / visibility / pointer-events）。
 *
 * 依赖：
 * - jQuery（用于 DOM 注入与查询）
 *
 * 关键 DOM 结构：
 * - .country-selecor                 目标挂载容器
 * - .country-selector-init-element   点击触发区域
 * - #lang_popup                      弹窗容器
 * - .popup_pp                        弹窗主体
 * - .close_pp                        关闭按钮
 *
 * 市场配置：
 * 每个市场通过 `.country-selector-market` 元素定义：
 *
 * data-parameter   URL 匹配参数
 * data-image       国家图片
 * data-language    语言文本
 * data-currency    货币文本
 *
 * URL 匹配规则：
 * /parameter
 * /parameter/xxx
 *
 * 示例：
 * /us
 * /us/products/xxx
 */

class CountrySelector extends HTMLElement {
    connectedCallback() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
        } else {
            this.init();
        }
    }

    init() {
        const target = document.querySelector('.country-selecor');

        if (!target) return;

        const $ = window.jQuery;

        if (!$) return;

        const $target = this.copyContentToTarget(target);

        const popup = $target[0].querySelector('#lang_popup');

        if (!popup) return;

        $target.off('click.countrySelector').on('click.countrySelector', (e) => {
            if (e.target.closest('.country-selector-init-element')) {
                e.stopPropagation();
                popup.classList.add('is-open');
            }
        });

        popup.addEventListener('click', (e) => {
            const clicked = e.target;

            e.stopPropagation();

            if (clicked.closest('.close_pp')) {
                e.preventDefault();
                popup.classList.remove('is-open');
                return;
            }

            if (clicked.closest('.popup_pp')) {
                return;
            }

            popup.classList.remove('is-open');
        });

        this.style.display = 'none';
    }

    copyContentToTarget(target) {
        const $ = window.jQuery;
        const $target = $(target);

        const currentPath = window.location.pathname;

        const marketElements = this.querySelectorAll('.country-selector-market');

        let matchedMarket = null;

        for (const el of marketElements) {
            const param = el.getAttribute('data-parameter');

            if (!param) continue;

            if (currentPath === `/${param}` || currentPath.startsWith(`/${param}/`)) {
                matchedMarket = el;
                break;
            }
        }

        $target
            .html(this.innerHTML)
            .addClass('country-selecor-active');

        if (!matchedMarket) {
            return $target;
        }

        const img = matchedMarket.getAttribute('data-image');
        const lang = matchedMarket.getAttribute('data-language');
        const currency = matchedMarket.getAttribute('data-currency');

        const $initElement = $target.find('.country-selector-init-element');

        if (img) {
            const $img = $initElement.find('img');

            if ($img.length) {
                $img.attr({
                    src: img,
                    srcset: '',
                    alt: lang || ''
                });
            }
        }

        if (lang) {
            $initElement
                .find('.language')
                .html(`&nbsp;/&nbsp;${lang}&nbsp;/&nbsp;`);
        }

        if (currency) {
            $initElement
                .find('.currency')
                .text(currency);
        }

        return $target;
    }
}

if (!customElements.get('country-selector')) {
    customElements.define('country-selector', CountrySelector);
}